import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { queryClient } from '../../../app/AppRoutes';
import { AppThunk } from '../../../app/store';
import {
  apiEndPoints,
  carrierEligibilityRetryEndPoint,
  deleteAttachmentEndpoint,
  deleteDocumentEndPoint,
  evidenceRetryEndPoint,
  getETIEndPoint,
  getMarkTaskOutdatedEndpoint,
  getRequestRuleReviewEndPoint,
  getTaskEndPoint,
  getTaskUpdateEndPoint,
  sendEmailEndPoint,
  taskByTaskTypeEndPoint,
  uploadAttachmentEndPoint,
  uploadEvidenceEndPoint,
  uploadLetterEndPoint,
} from '../../../common/api.endPoints';
import { Resource } from '../../../common/constants/resource';
import { SharedConstants } from '../../../common/constants/shared';
import { TaskState } from '../../../common/constants/states';
import { HttpService } from '../../../common/http.service';
import { syncApplicationState } from '../../detail/detailSlice';
import { getQueryKey } from '../../detail/tasks/etiGenerator/hooks/useETI';
import { ETIFormData } from '../../detail/tasks/etiGenerator/types';
import {
  ISnacksProps,
  SnackBarType,
} from '../../layout/interfaces/ISnacksProps';
import { IOrderResponse } from '../interfaces/IOrderResponse';
import { ITask } from '../interfaces/ITask';
import { IWaiveReasonResponse } from '../interfaces/IWaiveReasonResponse';
import { TaskTypes } from '../types/taskTypes';
import { EvidenceOrder } from './evidenceOrder';
import { taskListActions } from './taskListSlice';
import { taskUiActions } from './taskUiSlice';

export const refreshTasks =
  (applicationId: string): AppThunk =>
  async dispatch => {
    const data = await HttpService.getInstance().get(getTaskEndPoint(applicationId));
    if (data && !data.error) {
      const tasks: any[] = data.parsedBody;
      dispatch(taskListActions.setTasks({ tasks: tasks.map(task => transformTask(task)) }));
    }
  };

export const fetchTasks =
  (
    id: string,
    setTaskFetchStatus: (status: string) => void,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    setTaskFetchStatus('loading');
    const data = await HttpService.getInstance().get(getTaskEndPoint(id));
    if (data && !data.error) {
      const tasks: any[] = data.parsedBody;
      dispatch(
        taskListActions.fetchTasksSuccess({
          tasks: tasks.map(task => transformTask(task)),
        }),
      );
      setTaskFetchStatus('success');
    } else {
      setTaskFetchStatus('error');
      if (data && data.error) {
        setSnacksCallback([
          ...openSnacks,
          {
            id: uuidv4(),
            type: data.error.type,
            message: data.error.message,
          },
        ]);
      }
    }
  };

export const fetchWaiveReasons =
  (
    setWaiveReasonsFetchStatus: (status: string) => void,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
    taskType?: string,
  ): AppThunk =>
  async dispatch => {
    setWaiveReasonsFetchStatus('loading');
    const url = taskType
      ? `${apiEndPoints.waiveReasons}?taskType=${taskType}`
      : apiEndPoints.waiveReasons;
    const data = await HttpService.getInstance().get(url);
    if (data && !data.error) {
      const reasons: IWaiveReasonResponse[] = data.parsedBody;
      dispatch(
        taskUiActions.fetchWaiveReasonsSuccess({ waiveReasons: reasons }),
      );
      setWaiveReasonsFetchStatus('success');
    } else {
      setWaiveReasonsFetchStatus('error');
      if (data && data.error) {
        setSnacksCallback([
          ...openSnacks,
          {
            id: uuidv4(),
            type: data.error.type,
            message: data.error.message,
          },
        ]);
      }
    }
  };

export const uploadEvidence =
  (
    applicationId: string,
    existingTaskData: ITask,
    formData: FormData,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    try {
      dispatch(
        taskListActions.updateTaskUploadStatus({
          id: existingTaskData.id,
          uploading: true,
          documentType: formData.get('documentType') as string,
          existingTaskData,
        }),
      );
      const response = await HttpService.getInstance().postFiles(
        uploadEvidenceEndPoint(applicationId, existingTaskData.id),
        formData,
      );
      if (response.response && response.response.length) {
        dispatch(
          taskListActions.addEvidence({
            id: response.id,
            state: response.taskState,
            evidence: response.response,
            existingTask: existingTaskData,
          }),
        );
        // dispatch(
        //   detailActions.onApplicationStageUpdate({
        //     applicationStage: response.applicationStage,
        //     applicationLifecycle: response.applicationLifecycle,
        //   }),
        // );
        dispatch(syncApplicationState(applicationId));
      }
    } catch (error) {
      setSnacksCallback([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message:
            (error as any).statusCode === SharedConstants.FORBIDDEN
              ? Resource.FORBIDDEN_ERROR
              : Resource.UPLOAD_ERROR,
        },
      ]);
    } finally {
      dispatch(
        taskListActions.updateTaskUploadStatus({
          id: existingTaskData.id,
          uploading: false,
          documentType: formData.get('documentType') as string,
          existingTaskData,
        }),
      );
    }
  };

export const deleteEvidence =
  (
    applicationId: string,
    existingTaskData: ITask,
    documentId: number,
    reason: string,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    try {
      const postData = { reason, applicationId, taskId: existingTaskData.id };
      const response = await HttpService.getInstance().post(
        deleteDocumentEndPoint(documentId),
        postData,
      );
      if (!response.error) {
        dispatch(
          taskListActions.deleteDocument({
            existingTask: existingTaskData,
            documentId,
          }),
        );
      }
    } catch (error) {
      setSnacksCallback([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message:
            (error as any).statusCode === SharedConstants.FORBIDDEN
              ? Resource.FORBIDDEN_ERROR
              : Resource.DELETE_ERROR,
        },
      ]);
    }
  };

export const deleteDisputeAttachment =
  (
    existingTaskData: ITask,
    documentId: number,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    try {
      const data = await HttpService.getInstance().patch(
        deleteAttachmentEndpoint(
          existingTaskData.id,
          TaskTypes.DISPUTE,
          documentId,
        ),
      );
      if (data && !data.error) {
        dispatch(
          taskListActions.deleteDocument({
            existingTask: existingTaskData,
            documentId,
          }),
        );
      }
    } catch (error) {
      setSnacksCallback([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message:
            (error as any).statusCode === SharedConstants.FORBIDDEN
              ? Resource.FORBIDDEN_ERROR
              : Resource.DELETE_ERROR,
        },
      ]);
    }
  };

export const uploadLetter =
  (
    applicationId: string,
    taskId: number,
    formData: FormData,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    try {
      dispatch(
        taskListActions.updateTaskUploadStatus({
          id: taskId,
          uploading: true,
          documentType: 'LETTER',
        }),
      );
      const response = await HttpService.getInstance().postFiles(
        uploadLetterEndPoint(applicationId, taskId),
        formData,
      );
      if (response) {
        dispatch(taskListActions.addAttachment(response));
        if (response.paUploadWarning) {
          setSnacksCallback((prev: ISnacksProps[]) => [
            ...prev,
            {
              id: uuidv4(),
              type: SnackBarType.ALERT,
              message: response.paUploadWarning,
            },
          ]);
        }
      }
    } catch (error) {
      setSnacksCallback([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message: Resource.UPLOAD_ERROR,
        },
      ]);
    } finally {
      dispatch(
        taskListActions.updateTaskUploadStatus({
          id: taskId,
          uploading: false,
          documentType: 'LETTER',
        }),
      );
    }
  };

export const uploadAttachment =
  (
    applicationId: string,
    taskId: number,
    formData: FormData,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    const documentType = formData.get('documentType') as string;
    try {
      dispatch(
        taskListActions.updateTaskUploadStatus({
          id: taskId,
          uploading: true,
          documentType,
        }),
      );
      const response = await HttpService.getInstance().postFiles(
        uploadAttachmentEndPoint(applicationId, taskId),
        formData,
      );
      if (response) {
        dispatch(taskListActions.addAttachment(response));
      }
    } catch (error) {
      setSnacksCallback([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message:
            (error as any).statusCode === SharedConstants.FORBIDDEN
              ? Resource.FORBIDDEN_ERROR
              : Resource.UPLOAD_ERROR,
        },
      ]);
    } finally {
      dispatch(
        taskListActions.updateTaskUploadStatus({
          id: taskId,
          uploading: false,
          documentType,
        }),
      );
    }
  };

export const evidenceInfoRetry =
  (
    applicationId: string,
    taskId: number,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    const data = await HttpService.getInstance().patch(
      evidenceRetryEndPoint(applicationId, taskId),
    );
    const taskAndApplicationStatus = data.parsedBody;
    if (data && !data.error) {
      dispatch(taskListActions.updateTaskState(taskAndApplicationStatus.task));
      // dispatch(
      //   detailActions.onApplicationStageUpdate({
      //     applicationStage: taskAndApplicationStatus.applicationStage,
      //     applicationLifecycle: taskAndApplicationStatus.applicationLifecycle,
      //   }),
      // );
      dispatch(syncApplicationState(applicationId));
    } else {
      if (data && data.error) {
        setSnacksCallback([
          ...openSnacks,
          {
            id: uuidv4(),
            type: data.error.type,
            message: data.error.message,
          },
        ]);
      }
    }
  };

export const carrierEligibilityRetry =
  (
    applicationId: string,
    taskId: number,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    const data = await HttpService.getInstance().patch(
      carrierEligibilityRetryEndPoint(applicationId, taskId),
    );
    const taskAndApplicationStatus = data.parsedBody;
    if (data && !data.error) {
      dispatch(
        taskListActions.onTaskStateUpdateSuccess(taskAndApplicationStatus.task),
      );
      // dispatch(
      //   detailActions.onApplicationStageUpdate({
      //     applicationStage: taskAndApplicationStatus.applicationStage,
      //     applicationLifecycle: taskAndApplicationStatus.applicationLifecycle,
      //   }),
      // );
      dispatch(syncApplicationState(applicationId));
    } else {
      if (data && data.error) {
        setSnacksCallback([
          ...openSnacks,
          {
            id: uuidv4(),
            type: data.error.type,
            message: data.error.message,
          },
        ]);
      }
    }
  };

export const markTaskOutdated =
  (
    applicationId: string,
    taskId: number,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    const data = await HttpService.getInstance().patch(
      getMarkTaskOutdatedEndpoint(applicationId, taskId),
    );
    const taskAndApplicationStatus = data.parsedBody;

    if (data && !data.error) {
      dispatch(taskListActions.updateTaskState(taskAndApplicationStatus.task));
      // dispatch(
      //   detailActions.onApplicationStageUpdate({
      //     applicationStage: taskAndApplicationStatus.applicationStage,
      //     applicationLifecycle: taskAndApplicationStatus.applicationLifecycle,
      //   }),
      // );
      dispatch(syncApplicationState(applicationId));
    } else {
      if (data && data.error) {
        setSnacksCallback([
          ...openSnacks,
          {
            id: uuidv4(),
            type: data.error.type,
            message: data.error.message,
          },
        ]);
      }
    }
  };

export const orderTaskByTaskType =
  (
    applicationId: string,
    taskType: TaskTypes,
    order: EvidenceOrder,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    const data = await HttpService.getInstance().post(
      taskByTaskTypeEndPoint(applicationId, taskType),
      order,
    );

    const taskAndApplicationStatus = data.parsedBody;
    if (data && !data.error) {
      dispatch(
        taskListActions.addTask({
          item: transformTask(taskAndApplicationStatus.task),
        }),
      );
      // dispatch(
      //   detailActions.onApplicationStageUpdate({
      //     applicationStage: taskAndApplicationStatus.applicationStage,
      //     applicationLifecycle: taskAndApplicationStatus.applicationLifecycle,
      //   }),
      // );
      dispatch(syncApplicationState(applicationId));
    } else {
      if (data && data.error) {
        setSnacksCallback([
          ...openSnacks,
          {
            id: uuidv4(),
            type: data.error.type,
            message: data.error.message,
          },
        ]);
      }
    }
  };

export const updateTaskState =
  (
    applicationId: string,
    tid: number,
    state: string,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
    reason?: string,
    sufficient?: boolean,
    feedback?: string,
  ): AppThunk =>
  async dispatch => {
    const requestBody = { taskState: state, reason, sufficient, feedback };
    const data = await HttpService.getInstance().patch(
      getTaskUpdateEndPoint(applicationId, tid),
      JSON.stringify(requestBody),
    );
    if (data && !data.error) {
      dispatch(taskListActions.onTaskStateUpdateSuccess(data.parsedBody));
      // dispatch(
      //   detailActions.onApplicationStageUpdate({
      //     applicationStage: data.parsedBody.applicationStage,
      //     applicationLifecycle: data.parsedBody.applicationLifecycle,
      //   }),
      // );
      dispatch(syncApplicationState(applicationId));

      // If is ETI task, invalidate the ETI query cache to refetch updated data
      if (data.parsedBody?.taskType === TaskTypes.ETI) {
        queryClient.invalidateQueries({
          queryKey: getQueryKey(tid),
        });
      }

      // Updating task state may result in new tasks being created e.g. APS backup for EHR.
      (data.parsedBody?.taskResponses ?? []).forEach((item: IOrderResponse) => {
        if (item.status === 201) {
          if (item.task !== null) {
            dispatch(
              taskListActions.addTask({ item: transformTask(item.task) }),
            );
          }
        } else {
          dispatch(taskUiActions.onOrderEvidenceFailed(item.error));
        }
      });
    } else {
      setSnacksCallback([...openSnacks, data.error]);
    }
  };

export const resetApplication = (): AppThunk => async dispatch => {
  dispatch(taskListActions.clearTasks({}));
  dispatch(taskUiActions.clearTaskMeta({}));
};

export const addTasks =
  (tasks: ITask[]): AppThunk =>
  async dispatch => {
    tasks.forEach(task => {
      dispatch(taskListActions.addTask({ item: transformTask(task) }));
    });
  };

export const updateTasksState =
  (tasks: ITask[]): AppThunk =>
  async dispatch => {
    tasks.forEach(t => {
      dispatch(
        taskListActions.updateTaskState({
          id: t.id,
          state: t.state,
          outdated: t.outdated,
          createdAt: t.createdAt ? t.createdAt : '',
          retriedAt: t.retriedAt,
        }),
      );
    });
  };

export const placeOrder =
  (
    id: string,
    orders: EvidenceOrder[],
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    const response = await HttpService.getInstance().post(
      getTaskEndPoint(id),
      orders,
    );

    if (response.error) {
      setSnacksCallback([...openSnacks, response.error]);
      return;
    }

    const data = response.parsedBody;

    // If an evidence was ordered successfully, there should be an application stage in its response
    // Backend actually returns applicationLifecycleV2 and applicationStageV2
    // TODO: change the names in the backend api later.
    const applicationStageV2 = data.applicationStage;
    const applicationLifecycleV2 = data.applicationLifecycle;
    if (applicationStageV2 && applicationLifecycleV2) {
      // dispatch(
      //   detailActions.onApplicationStageUpdate({
      //     applicationStageV2,
      //     applicationLifecycle,
      //   }),
      // );
      dispatch(syncApplicationState(id));
    }

    if (isMultiStatusResponse(response)) {
      // Check each response to see which evidences were successfully ordered
      data.taskResponses.forEach((item: IOrderResponse) => {
        if (item.status === 201) {
          if (item.task !== null) {
            dispatch(
              taskListActions.addTask({ item: transformTask(item.task) }),
            );
          }
        } else {
          dispatch(taskUiActions.onOrderEvidenceFailed(item.error));
        }
      });
    } else {
      dispatch(
        taskUiActions.onOrderEvidenceFailed(
          response ? response.error : undefined,
        ),
      );
    }
  };

export const requestRuleReview =
  (
    id: string,
    orderData: object,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    const data = await HttpService.getInstance().put(
      getRequestRuleReviewEndPoint(id),
      orderData,
    );

    const taskAndApplicationStatus = data.parsedBody;
    if (data && !data.error) {
      dispatch(
        taskListActions.addTask({
          item: transformTask(taskAndApplicationStatus.task),
        }),
      );
      // dispatch(
      //   detailActions.onApplicationStageUpdate({
      //     applicationStage: taskAndApplicationStatus.applicationStage,
      //     applicationLifecycle: taskAndApplicationStatus.applicationLifecycle,
      //   }),
      // );
      dispatch(syncApplicationState(id));
    } else {
      if (data && data.error) {
        setSnacksCallback([
          ...openSnacks,
          {
            id: uuidv4(),
            type: data.error.type,
            message: data.error.message,
          },
        ]);
      }
    }
  };

const isMultiStatusResponse = (response: any) => {
  return response && !response.error && response.status === 207;
};

const transformTask = (data: any): ITask => ({
  id: parseInt(data.id, 10),
  taskType: data.taskType,
  taskData: data.taskData,
  state: data.state,
  sufficient: data.sufficient,
  feedback: data.feedback,
  outdated: data.outdated ?? false,
  retriedAt: data.retriedAt ?? null,
  createdAt: data.createdAt ?? null,
  evidence: data.evidence,
  attachment: data.attachment,
  evidenceStateInfo: data.evidenceStateInfo,
  evidenceId: data.evidenceId,
  waiveReason: data.waiveReason ?? null,
  updatedAt: data.updatedAt,
  ongoingUpload: {},
});

export const updateTaskData =
  (
    applicationId: string,
    tid: number,
    taskData: object,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    const requestBody = { taskData };
    const data = await HttpService.getInstance().patch(
      getTaskUpdateEndPoint(applicationId, tid),
      JSON.stringify(requestBody),
    );
    if (data && !data.error) {
      dispatch(taskListActions.onTaskStateUpdateSuccess(data.parsedBody));
      // dispatch(
      //   detailActions.onApplicationStageUpdate({
      //     applicationStage: data.parsedBody.applicationStage,
      //     applicationLifecycle: data.parsedBody.applicationLifecycle,
      //   }),
      // );
      dispatch(syncApplicationState(applicationId));
    } else {
      setSnacksCallback([...openSnacks, data.error]);
    }
  };

export const deleteAttachment =
  (taskId: number, taskType: TaskTypes, attachmentId: number): AppThunk =>
  async dispatch => {
    const data = await HttpService.getInstance().patch(
      deleteAttachmentEndpoint(taskId, taskType, attachmentId),
    );

    if (data && !data.error) {
      dispatch(taskListActions.deleteAttachment({ taskId }));
    }
  };

export interface UpsertETIArgs {
  formData: ETIFormData;
  applicationId: string;
  openSnacks: ISnacksProps[];
  updateSnacks: (updatedSnacks: any) => void;
}

export const upsertETI = createAsyncThunk<
  boolean,
  UpsertETIArgs,
  { rejectValue: boolean }
>('tasks/upsertETI', async (args, thunkApi) => {
  const { formData, applicationId, openSnacks, updateSnacks } = args;
  const { dispatch, rejectWithValue } = thunkApi;
  try {
    const response = await HttpService.getInstance().post(
      getETIEndPoint(formData.taskId),
      formData,
    );

    if (response.error) {
      updateSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message: response.error.message || 'Failed to save ETI',
          autoHideDuration: 10000,
        },
      ]);
      return rejectWithValue(false);
    }

    // Keep ETI in sync
    queryClient.setQueryData(getQueryKey(formData.taskId), response);

    const successMessage =
      formData.status === 'PUBLISHED'
        ? 'ETI has been published successfully'
        : 'ETI has been saved successfully';

    const data = await HttpService.getInstance().get(
      getTaskEndPoint(applicationId),
    );
    if (data && !data.error) {
      const tasks: any[] = data.parsedBody;
      dispatch(
        taskListActions.setTasks({
          tasks: tasks.map(task => transformTask(task)),
        }),
      );
    } else if (data && data.error) {
      updateSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: data.error.type,
          message: data.error.message,
        },
      ]);
      return rejectWithValue(false);
    }

    // Only show success message when both ETI endpoint and task endpoint are successful
    updateSnacks([
      ...openSnacks,
      {
        id: uuidv4(),
        type: SnackBarType.SUCCESS,
        message: successMessage,
        autoHideDuration: 2500,
      },
    ]);

    return true;
  } catch (error) {
    updateSnacks([
      ...openSnacks,
      {
        id: uuidv4(),
        type: 'error',
        message: 'Network error occurred while saving ETI. Please try again.',
      },
    ]);
    return rejectWithValue(false);
  }
});

export const sendEmail =
  (
    applicationId: string,
    taskId: number,
    termPolicyId: number,
    openSnacks: any,
    updateSnacks: (updatedSnacks: any) => void,
    reason?: string,
  ): AppThunk =>
  async dispatch => {
    const { parsedBody } = await HttpService.getInstance().patch(
      sendEmailEndPoint(applicationId, taskId, termPolicyId),
    );

    const isSent = parsedBody.labelIds?.includes('SENT') ?? false;
    const newTaskState: TaskState = isSent
      ? TaskState.COMPLETED
      : TaskState.FOR_REVIEW;

    dispatch(
      updateTaskState(
        applicationId,
        taskId,
        newTaskState,
        openSnacks,
        updateSnacks,
        reason,
      ),
    );
  };
